import { useApiQuery } from "@/hooks/useApiQuery";
import {
  CrmIntegrationProviderSupported,
  CrmRecordPresenceDTO,
} from "@shaarp/common";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useEffectEvent } from "react";

type UseCrmRecordPresenceOptions = {
  /** The check route of the company or contact the menu belongs to. */
  readonly path: string;
  /** Whether the CRM menu is open: the check only runs then. */
  readonly isOpen: boolean;
  /** Whether the menu shows a record as sent to at least one CRM. */
  readonly hasLinkedRecord: boolean;
  /** Receives the CRMs that no longer hold the record the menu showed. */
  readonly onDeleted: (
    deletedIn: readonly CrmIntegrationProviderSupported[],
  ) => void;
};

/**
 * Asks, each time a CRM menu opens on a record it shows as sent, whether the
 * CRM still holds that record — and reports the CRMs that deleted it.
 *
 * The statuses a page receives come from Shaarp's mirror of each CRM, which a
 * deletion made in the CRM never reaches: the record would read as sent
 * forever, with nothing offering to send it again. The API forgets those links
 * as it answers, so the next page load agrees with what `onDeleted` reported.
 *
 * Nothing is kept once the answer is read: a cached answer would unlink a
 * record sent again since, and every opening asks afresh. A failed check
 * reports nothing — the menu keeps what the mirror says.
 *
 * @returns `cancel`, to drop a check in flight when a send starts: the send is
 *   newer than whatever the check would report.
 */
export function useCrmRecordPresence({
  path,
  isOpen,
  hasLinkedRecord,
  onDeleted,
}: UseCrmRecordPresenceOptions): { cancel: () => void } {
  const queryClient = useQueryClient();
  const key = ["crm-record-presence", path];

  const { data } = useApiQuery<CrmRecordPresenceDTO>({
    key,
    path,
    enabled: isOpen && hasLinkedRecord,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    // A new object for every answer, even an identical one: the record may
    // have been sent again, then deleted again, between two openings.
    structuralSharing: false,
  });

  const reportDeleted = useEffectEvent(
    (deletedIn: readonly CrmIntegrationProviderSupported[]) =>
      onDeleted(deletedIn),
  );

  useEffect(() => {
    if (data && data.deletedIn.length > 0) reportDeleted(data.deletedIn);
  }, [data]);

  return {
    cancel: () => void queryClient.cancelQueries({ queryKey: key }),
  };
}
